import { Spacing } from '@persona-id/design-tokens';
import { RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { ChangeEvent } from 'react';
import styled from 'styled-components';

import Checkbox from 'design-system/components/Checkbox';

const CheckboxContainer = styled.div`
  margin: ${Spacing[2]};
`;

const InlineLabel = styled.label`
  display: inline-block;
`;

function selectValue(value: any, selected: any[], all: any[]) {
  const at = all.indexOf(value);
  const updated = selected.slice(0, at).concat(value, selected.slice(at));
  // As inserting values at predefined index positions doesn't work with empty
  // arrays, we need to reorder the updated selection to match the initial order
  return updated.sort((a, b) => Number(all.indexOf(a) > all.indexOf(b)));
}

function deselectValue(value: any, selected: any[]) {
  return selected.filter((v) => v !== value);
}

/** The `CheckboxesWidget` is a widget for rendering checkbox groups.
 *  It is typically used to represent an array of enums.
 *
 * @param props - The `WidgetProps` for this component
 */
export function CheckboxesWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F = any>({
  id,
  disabled,
  options: { inline = false, enumOptions, enumDisabled },
  value,
  autofocus = false,
  readonly,
  onChange,
}: /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
WidgetProps<T, S, F>) {
  return (
    <div id={id}>
      {Array.isArray(enumOptions) &&
        enumOptions.map((option, index) => {
          const checked = value.indexOf(option.value) !== -1;
          const itemDisabled =
            Array.isArray(enumDisabled) && enumDisabled.indexOf(option.value) !== -1;

          const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            const all = enumOptions.map(({ value }) => value);
            if (event.target.checked) {
              onChange(selectValue(option.value, value, all));
            } else {
              onChange(deselectValue(option.value, value));
            }
          };

          const checkbox = (
            <Checkbox
              label={option.label}
              id={`${id}-${option.value}`}
              name={id}
              checked={checked}
              disabled={disabled || itemDisabled || readonly}
              autoFocus={autofocus && index === 0}
              onChange={handleChange}
            />
          );
          return inline ? (
            <InlineLabel key={option.value}>{checkbox}</InlineLabel>
          ) : (
            <CheckboxContainer key={option.value}>
              <label>{checkbox}</label>
            </CheckboxContainer>
          );
        })}
    </div>
  );
}
